/** @file Hook: the Tree's roving active row and its key handling (arrows, Home/End, Enter/Space, F2). */
import { useState, type KeyboardEvent, type RefObject } from 'react';
import type { TreeRow } from './logic/flattenTree';
import { hasCommandModifier } from './logic/hasCommandModifier';
import { resolveActiveId } from './logic/resolveActiveId';
import { revealScrollTop } from './logic/revealScrollTop';
import { treeKeyAction } from './logic/treeKeyAction';

/** Inputs of useTreeNavigation. */
export interface TreeNavOptions {
  rows: readonly TreeRow[];
  selected: ReadonlySet<string>;
  rowHeight: number;
  viewportHeight: number;
  scrollRef: RefObject<HTMLElement | null>;
  /** Told the new scrollTop right after a keyboard move scrolls the tree, so the rows in view render without waiting for the scroll event. */
  onScrollTop: (scrollTop: number) => void;
  onToggle: (id: string, expanded: boolean) => void;
  onSelect: (id: string, additive: boolean) => void;
  onRenameStart?: ((id: string) => void) | undefined;
}

/** Id `aria-activedescendant` points at (null = none) and the keydown handler for the tree element. */
export interface TreeNav {
  activeId: string | null;
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
}

/** True for a key pressed on the tree element itself (not on a rename input inside it) without Ctrl, Cmd or Alt. */
function isPlainKeyOnTree(e: KeyboardEvent<HTMLElement>): boolean {
  return e.target === e.currentTarget && !hasCommandModifier(e);
}

/** Keys act on the tree element itself (never on a rename input inside it); moving focus also selects that row. A consumed key does not reach the global shortcuts. */
export function useTreeNavigation(o: TreeNavOptions): TreeNav {
  const [remembered, setRemembered] = useState<string | null>(null);
  const activeId = resolveActiveId(o.rows, remembered, o.selected);
  const reveal = (id: string): void => {
    const el = o.scrollRef.current;
    const index = o.rows.findIndex((row) => row.id === id);
    if (!el || index < 0) return;
    el.scrollTop = revealScrollTop(el.scrollTop, o.viewportHeight, o.rowHeight, index);
    o.onScrollTop(el.scrollTop);
  };
  const focusRow = (id: string): void => {
    setRemembered(id);
    reveal(id);
    o.onSelect(id, false);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLElement>): void => {
    if (!isPlainKeyOnTree(e)) return;
    const action = treeKeyAction(o.rows, activeId, e.code);
    if (action.kind === 'none') return;
    e.preventDefault();
    e.stopPropagation();
    switch (action.kind) {
      case 'focus': focusRow(action.id); break;
      case 'toggle': o.onToggle(action.id, action.expanded); break;
      case 'select': o.onSelect(action.id, false); break;
      case 'rename': reveal(action.id); o.onRenameStart?.(action.id); break;
    }
  };
  return { activeId, onKeyDown };
}
